import React, { useState } from 'react';
import { Globe, Target, DollarSign, TrendingUp, Map, Users } from 'lucide-react';

const InternationalMarketingGuide = () => {
  const [selectedRegion, setSelectedRegion] = useState('all');

  const platformsByRegion = {
    'brasil': {
      name: '🇧🇷 Brasil',
      platforms: [
        { name: 'Hotmart', icon: '🔥', description: 'Infoprodutos brasileiros', commission: '20-70%' },
        { name: 'Monetizze', icon: '💰', description: 'Produtos digitais BR', commission: '30-80%' },
        { name: 'Magazine Você', icon: '🏪', description: 'E-commerce nacional', commission: '2-8%' },
        { name: 'Mercado Livre', icon: '💛', description: 'Marketplace BR', commission: '1-12%' }
      ],
      socialNetworks: ['Facebook', 'Instagram', 'TikTok', 'YouTube'],
      targetAudience: 'Público brasileiro falante de português',
      tips: [
        'Use português brasileiro no conteúdo',
        'Destaque pagamento via PIX',
        'Fale sobre entrega nacional rápida',
        'Use referências culturais brasileiras'
      ]
    },
    'eua': {
      name: '🇺🇸 Estados Unidos',
      platforms: [
        { name: 'ClickBank', icon: '💳', description: 'Produtos digitais globais', commission: '50-75%' },
        { name: 'Amazon Associates', icon: '📦', description: 'E-commerce global', commission: '1-10%' },
        { name: 'Awin', icon: '🌐', description: 'Marcas internacionais', commission: '2-20%' }
      ],
      socialNetworks: ['Facebook', 'Instagram', 'TikTok', 'YouTube', 'Twitter'],
      targetAudience: 'Público americano falante de inglês',
      tips: [
        'Use inglês americano no conteúdo',
        'Destaque frete grátis e Amazon Prime',
        'Mencione garantias e políticas de devolução',
        'Use Black Friday e Cyber Monday como ganchos'
      ]
    },
    'europa': {
      name: '🇪🇺 Europa',
      platforms: [
        { name: 'ClickBank', icon: '💳', description: 'Multi-idioma', commission: '50-75%' },
        { name: 'Amazon Associates', icon: '📦', description: 'Amazon.co.uk, .de, .fr', commission: '1-10%' },
        { name: 'Awin', icon: '🌐', description: 'Marcas europeias', commission: '2-20%' }
      ],
      socialNetworks: ['Facebook', 'Instagram', 'YouTube'],
      targetAudience: 'Público europeu (UK, Alemanha, França, Espanha, Itália)',
      tips: [
        'Adapte o idioma para cada país (UK=inglês, DE=alemão, FR=francês)',
        'Destaque conformidade com GDPR',
        'Mencione entrega na União Europeia',
        'Use euro (€) nos preços quando possível'
      ]
    },
    'latam': {
      name: '🌎 América Latina',
      platforms: [
        { name: 'Mercado Livre', icon: '💛', description: 'Marketplace LATAM', commission: '1-12%' },
        { name: 'Shopee', icon: '🛒', description: 'E-commerce regional', commission: '3-15%' },
        { name: 'ClickBank', icon: '💳', description: 'Produtos digitais', commission: '50-75%' }
      ],
      socialNetworks: ['Facebook', 'Instagram', 'TikTok', 'WhatsApp'],
      targetAudience: 'Público latino-americano (México, Argentina, Chile, Colômbia)',
      tips: [
        'Use espanhol neutro (fácil entendimento regional)',
        'Destaque opções de parcelamento',
        'Mencione entregas regionais',
        'Use WhatsApp como canal de suporte'
      ]
    },
    'asia': {
      name: '🌏 Ásia',
      platforms: [
        { name: 'Shopee', icon: '🛒', description: 'Líder regional', commission: '3-15%' },
        { name: 'ClickBank', icon: '💳', description: 'Digital global', commission: '50-75%' }
      ],
      socialNetworks: ['TikTok', 'Instagram', 'Facebook', 'YouTube'],
      targetAudience: 'Público asiático (Sudeste Asiático principalmente)',
      tips: [
        'Foque em países do Shopee (Singapura, Malásia, Filipinas, Tailândia)',
        'Use inglês simplificado',
        'Destaque promoções e descontos',
        'Aproveite eventos de compras (11.11, 12.12)'
      ]
    }
  };

  const regions = Object.keys(platformsByRegion);
  const currentRegion = selectedRegion === 'all' ? null : platformsByRegion[selectedRegion];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block p-4 bg-blue-500/20 rounded-full mb-4">
            <Globe className="h-16 w-16 text-blue-400" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">
            🌍 Guia de Marketing Internacional
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Aprenda a fazer campanhas de afiliados em qualquer país do mundo usando as 9 plataformas do MonetizeMaster
          </p>
        </div>

        {/* Region Selector */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8">
          <h2 className="text-white font-bold text-xl mb-4">📍 Selecione uma Região:</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <button
              onClick={() => setSelectedRegion('all')}
              className={`p-4 rounded-lg font-bold transition-all ${
                selectedRegion === 'all'
                  ? 'bg-blue-600 text-white scale-105'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              🌐 Todas
            </button>
            {regions.map(region => (
              <button
                key={region}
                onClick={() => setSelectedRegion(region)}
                className={`p-4 rounded-lg font-bold transition-all ${
                  selectedRegion === region
                    ? 'bg-purple-600 text-white scale-105'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                }`}
              >
                {platformsByRegion[region].name}
              </button>
            ))}
          </div>
        </div>

        {/* Overview */}
        {selectedRegion === 'all' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-900/40 to-purple-900/40 border border-blue-500/30 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-6">🎯 Como Funciona o Marketing Internacional</h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white/5 rounded-lg p-6">
                  <h3 className="text-yellow-400 font-bold text-lg mb-3">1️⃣ Escolha a Região</h3>
                  <p className="text-gray-300 text-sm">
                    Defina qual país ou região quer alcançar: Brasil, EUA, Europa, LATAM ou Ásia. 
                    Cada região tem plataformas específicas.
                  </p>
                </div>
                
                <div className="bg-white/5 rounded-lg p-6">
                  <h3 className="text-green-400 font-bold text-lg mb-3">2️⃣ Use Plataformas Certas</h3>
                  <p className="text-gray-300 text-sm">
                    <strong>Brasil:</strong> Hotmart, Monetizze, Magalu, Mercado Livre<br/>
                    <strong>Internacional:</strong> ClickBank, Amazon, Awin, Shopee
                  </p>
                </div>
                
                <div className="bg-white/5 rounded-lg p-6">
                  <h3 className="text-blue-400 font-bold text-lg mb-3">3️⃣ Gere Conteúdo no Idioma Local</h3>
                  <p className="text-gray-300 text-sm">
                    Use o <strong>Gerador de Conteúdo IA</strong> com 8 idiomas! 
                    Português para Brasil, Inglês para EUA, Espanhol para LATAM, etc.
                  </p>
                </div>
                
                <div className="bg-white/5 rounded-lg p-6">
                  <h3 className="text-purple-400 font-bold text-lg mb-3">4️⃣ Segmente Anúncios por País</h3>
                  <p className="text-gray-300 text-sm">
                    No Facebook Ads / TikTok Ads: escolha o país de destino. 
                    Conteúdo em inglês → Anúncios para EUA. Português → Brasil.
                  </p>
                </div>
              </div>

              <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-6">
                <p className="text-yellow-300 text-sm">
                  <strong>💡 Exemplo Prático:</strong> Você tem produto ClickBank em inglês sobre fitness. 
                  Gere conteúdo em inglês → Crie anúncios no Facebook segmentados para <strong>USA, UK, Canadá</strong>. 
                  Quando clicarem, serão direcionados para o link ClickBank que paga em dólar!
                </p>
              </div>
            </div>

            {/* Platform Summary */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-white font-bold text-xl mb-4">📊 Resumo: Qual Plataforma Usar em Cada País</h2>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="text-left py-3 px-4 text-gray-300">Plataforma</th>
                      <th className="text-left py-3 px-4 text-gray-300">Países/Regiões</th>
                      <th className="text-left py-3 px-4 text-gray-300">Idiomas</th>
                      <th className="text-left py-3 px-4 text-gray-300">Tipo</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    <tr className="border-b border-white/10">
                      <td className="py-3 px-4"><strong>🔥 Hotmart</strong></td>
                      <td className="py-3 px-4">🇧🇷 Brasil</td>
                      <td className="py-3 px-4">Português</td>
                      <td className="py-3 px-4">Infoprodutos</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-3 px-4"><strong>💰 Monetizze</strong></td>
                      <td className="py-3 px-4">🇧🇷 Brasil</td>
                      <td className="py-3 px-4">Português</td>
                      <td className="py-3 px-4">Digitais</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-3 px-4"><strong>💳 ClickBank</strong></td>
                      <td className="py-3 px-4">🌐 Global (195+ países)</td>
                      <td className="py-3 px-4">Multi-idioma</td>
                      <td className="py-3 px-4">Digital Global</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-3 px-4"><strong>📦 Amazon</strong></td>
                      <td className="py-3 px-4">🇺🇸 🇧🇷 🇬🇧 🇩🇪 🇫🇷 🇨🇦</td>
                      <td className="py-3 px-4">Multi-idioma</td>
                      <td className="py-3 px-4">E-commerce</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-3 px-4"><strong>🛒 Shopee</strong></td>
                      <td className="py-3 px-4">🇧🇷 Brasil + 🌏 Ásia</td>
                      <td className="py-3 px-4">PT, EN</td>
                      <td className="py-3 px-4">E-commerce</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-3 px-4"><strong>🏪 Magazine Você</strong></td>
                      <td className="py-3 px-4">🇧🇷 Brasil</td>
                      <td className="py-3 px-4">Português</td>
                      <td className="py-3 px-4">Varejo Nacional</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-3 px-4"><strong>💛 Mercado Livre</strong></td>
                      <td className="py-3 px-4">🌎 LATAM (BR, AR, MX, CL)</td>
                      <td className="py-3 px-4">PT, ES</td>
                      <td className="py-3 px-4">Marketplace</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4"><strong>🌐 Awin</strong></td>
                      <td className="py-3 px-4">🇺🇸 🇪🇺 🇬🇧 Global</td>
                      <td className="py-3 px-4">Multi-idioma</td>
                      <td className="py-3 px-4">Rede Global</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Region Detail */}
        {currentRegion && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-purple-900/40 to-blue-900/40 border border-purple-500/30 rounded-xl p-8">
              <h2 className="text-3xl font-bold text-white mb-6">{currentRegion.name}</h2>
              
              {/* Platforms for this region */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-yellow-400 mb-4">🎯 Plataformas Recomendadas:</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {currentRegion.platforms.map((platform, idx) => (
                    <div key={idx} className="bg-white/10 rounded-lg p-4 border border-white/20">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-3xl">{platform.icon}</span>
                        <div>
                          <h4 className="text-white font-bold">{platform.name}</h4>
                          <p className="text-gray-400 text-xs">{platform.description}</p>
                        </div>
                      </div>
                      <p className="text-green-400 text-sm font-bold">💰 Comissão: {platform.commission}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Social Networks */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-blue-400 mb-4">📱 Redes Sociais para Divulgar:</h3>
                <div className="flex flex-wrap gap-3">
                  {currentRegion.socialNetworks.map((network, idx) => (
                    <span key={idx} className="bg-blue-500/20 text-blue-300 px-4 py-2 rounded-full font-bold">
                      {network}
                    </span>
                  ))}
                </div>
              </div>

              {/* Target Audience */}
              <div className="mb-6 bg-white/5 rounded-lg p-4">
                <h3 className="text-lg font-bold text-purple-400 mb-2">👥 Público-Alvo:</h3>
                <p className="text-gray-300">{currentRegion.targetAudience}</p>
              </div>

              {/* Tips */}
              <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-6">
                <h3 className="text-xl font-bold text-green-400 mb-4">💡 Dicas Específicas:</h3>
                <ul className="space-y-2">
                  {currentRegion.tips.map((tip, idx) => (
                    <li key={idx} className="text-gray-300 flex items-start gap-2">
                      <span className="text-green-400 font-bold">✓</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* How to Execute */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 mt-8">
          <h2 className="text-white font-bold text-2xl mb-6">🚀 Como Executar Campanhas Internacionais</h2>
          
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 rounded-lg p-6">
              <h3 className="text-blue-400 font-bold text-lg mb-3">Passo 1: Configure as Plataformas Certas</h3>
              <p className="text-gray-300 mb-2">
                • Para Brasil: Hotmart, Monetizze, Magalu, Mercado Livre<br/>
                • Para Internacional: ClickBank, Amazon, Awin, Shopee
              </p>
              <a href="/setup" className="text-yellow-400 hover:underline">→ Ir para Setup APIs</a>
            </div>

            <div className="bg-gradient-to-r from-green-900/20 to-blue-900/20 rounded-lg p-6">
              <h3 className="text-green-400 font-bold text-lg mb-3">Passo 2: Gere Links de Produtos</h3>
              <p className="text-gray-300 mb-2">
                • Crie links de afiliado para os produtos que quer promover<br/>
                • Exemplo: produto fitness ClickBank em inglês para EUA
              </p>
              <a href="/links" className="text-yellow-400 hover:underline">→ Ir para Gerar Links</a>
            </div>

            <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 rounded-lg p-6">
              <h3 className="text-purple-400 font-bold text-lg mb-3">Passo 3: Crie Conteúdo no Idioma Certo</h3>
              <p className="text-gray-300 mb-2">
                • Use o Gerador de Conteúdo IA<br/>
                • Selecione o idioma do país: Inglês (EUA), Português (BR), Espanhol (LATAM)<br/>
                • IA gera conteúdo nativo e persuasivo
              </p>
              <a href="/content-generator" className="text-yellow-400 hover:underline">→ Ir para Gerador IA</a>
            </div>

            <div className="bg-gradient-to-r from-yellow-900/20 to-orange-900/20 rounded-lg p-6">
              <h3 className="text-yellow-400 font-bold text-lg mb-3">Passo 4: Segmente Anúncios por País</h3>
              <p className="text-gray-300 mb-3">
                <strong>No Facebook Ads Manager / TikTok Ads:</strong>
              </p>
              <ol className="text-gray-300 space-y-2 ml-4">
                <li>1. Crie uma campanha nova</li>
                <li>2. Em "Público" → Selecione o país de destino (USA, Brasil, etc.)</li>
                <li>3. Use o conteúdo gerado pela IA naquele idioma</li>
                <li>4. Adicione seu link de afiliado</li>
                <li>5. Publique!</li>
              </ol>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 mt-8">
          <h2 className="text-white font-bold text-2xl mb-6">❓ Perguntas Frequentes</h2>
          
          <div className="space-y-4">
            <details className="bg-white/5 rounded-lg p-4">
              <summary className="text-yellow-400 font-bold cursor-pointer">
                Como promover ClickBank no exterior se sou brasileiro?
              </summary>
              <p className="text-gray-300 mt-3 text-sm">
                Simples! Gere conteúdo em INGLÊS usando a IA, crie anúncios no Facebook/TikTok 
                segmentados para USA/UK, e use seu link ClickBank. Você recebe em dólar via PayPal ou transferência!
              </p>
            </details>

            <details className="bg-white/5 rounded-lg p-4">
              <summary className="text-yellow-400 font-bold cursor-pointer">
                Posso promover Hotmart no exterior?
              </summary>
              <p className="text-gray-300 mt-3 text-sm">
                Hotmart é focado em Brasil/Portugal. Para internacional, use ClickBank, Amazon ou Awin.
              </p>
            </details>

            <details className="bg-white/5 rounded-lg p-4">
              <summary className="text-yellow-400 font-bold cursor-pointer">
                Como segmento anúncios no Facebook para países específicos?
              </summary>
              <p className="text-gray-300 mt-3 text-sm">
                No Facebook Ads Manager: Criar Campanha → Definir Público → Localização → 
                Escolha o país (ex: United States). Só pessoas daquele país verão seu anúncio!
              </p>
            </details>

            <details className="bg-white/5 rounded-lg p-4">
              <summary className="text-yellow-400 font-bold cursor-pointer">
                Preciso de conta bancária americana para receber?
              </summary>
              <p className="text-gray-300 mt-3 text-sm">
                NÃO! ClickBank e Amazon pagam via PayPal, Payoneer ou transferência internacional. 
                Você recebe direto na sua conta brasileira.
              </p>
            </details>

            <details className="bg-white/5 rounded-lg p-4">
              <summary className="text-yellow-400 font-bold cursor-pointer">
                Qual plataforma paga melhor em dólar?
              </summary>
              <p className="text-gray-300 mt-3 text-sm">
                <strong>ClickBank</strong> é a melhor para dólar: comissões de 50-75%, pagamento via PayPal, 
                aceito em 195+ países. Produtos digitais = margens altas!
              </p>
            </details>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternationalMarketingGuide;
